import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Store, ShoppingCart, User, Heart, MessageCircle } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { useWishlist } from '../../contexts/WishlistContext';
import { useAuth } from '../../contexts/AuthContext';
import { useMessaging } from '../../contexts/MessagingContext';
import { getTotalUnreadCount } from '../../utils/messageUtils';
import { sanitizeString } from '../../utils/sanitization';
import { validateString } from '../../utils/validation';

// Local sanitizeNumber for navigation badge counts (specific use case)
function sanitizeNumber(input: any, min: number = 0, max: number = 999): number {
  const num = Number(input);
  if (isNaN(num)) return 0;
  return Math.max(min, Math.min(max, Math.floor(num)));
}

// Security: Enhanced rate limiting for navigation
class NavigationRateLimiter {
  private attempts: Map<string, number[]> = new Map();
  private readonly maxAttempts = 20; // 20 navigations per minute
  private readonly windowMs = 60000; // 1 minute
  private readonly maxAttemptsPerHour = 200; // 200 navigations per hour
  private readonly hourlyWindowMs = 3600000; // 1 hour

  isAllowed(key: string): { allowed: boolean; message?: string } {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    
    // Remove old attempts outside the window
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);
    const hourlyAttempts = attempts.filter(time => now - time < this.hourlyWindowMs);
    
    // Check hourly limit first
    if (hourlyAttempts.length >= this.maxAttemptsPerHour) {
      return {
        allowed: false,
        message: 'Too many navigation attempts in the last hour. Please wait before trying again.'
      };
    }
    
    if (recentAttempts.length >= this.maxAttempts) {
      return {
        allowed: false,
        message: 'Too many navigation attempts. Please try again later.'
      };
    }
    
    recentAttempts.push(now);
    this.attempts.set(key, recentAttempts);
    return { allowed: true };
  }

  getRemainingAttempts(key: string): number {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);
    return Math.max(0, this.maxAttempts - recentAttempts.length);
  }
}

const navigationRateLimiter = new NavigationRateLimiter();

const MobileBottomNav: React.FC = () => {
  const location = useLocation();
  const { currentUser } = useAuth();
  const { conversations } = useMessaging();
  
  // Security: Enhanced safe context access with error handling
  const cartContext = useCart();
  const wishlistContext = useWishlist();
  
  // Security: Enhanced sanitize context values with safe fallbacks
  const totalItems = sanitizeNumber(cartContext?.totalItems, 0, 999);
  const wishlistCount = sanitizeNumber(wishlistContext?.wishlistCount, 0, 999);
  const unreadCount = currentUser ? getTotalUnreadCount(conversations, currentUser.uid) : 0;

  // Handle messages button click
  const handleMessagesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Open chat widget by setting URL param
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set('messaging', 'true');
    window.history.pushState({}, '', `${window.location.pathname}?${currentParams.toString()}`);
    // Trigger a custom event for ChatWidget to listen to
    window.dispatchEvent(new CustomEvent('openChatWidget'));
  };

  // Security: Enhanced safe navigation items with sanitized data - memoized to prevent unnecessary recalculations
  const navItems = useMemo(() => {
    const items = [
      {
        path: '/',
        icon: Home,
        label: 'Home',
        active: location.pathname === '/',
        onClick: undefined as ((e: React.MouseEvent) => void) | undefined
      },
      {
        path: '/stores',
        icon: Store,
        label: 'Store',
        active: location.pathname.startsWith('/stores') || location.pathname.startsWith('/store-page'),
        onClick: undefined as ((e: React.MouseEvent) => void) | undefined
      },
      {
        path: '/wishlist',
        icon: Heart,
        label: 'Wishlist',
        active: location.pathname === '/wishlist',
        badge: wishlistCount > 0 ? wishlistCount : undefined,
        onClick: undefined as ((e: React.MouseEvent) => void) | undefined
      },
      {
        path: '/cart',
        icon: ShoppingCart,
        label: 'Cart',
        active: location.pathname === '/cart',
        badge: totalItems > 0 ? totalItems : undefined,
        onClick: undefined as ((e: React.MouseEvent) => void) | undefined
      }
    ];

    // Add Messages button if user is logged in
    if (currentUser) {
      items.push({
        path: '#',
        icon: MessageCircle,
        label: 'Messages',
        active: location.pathname === '/messaging' || location.search.includes('messaging=true'),
        badge: unreadCount > 0 ? unreadCount : undefined,
        onClick: handleMessagesClick
      });
    }

    // Add Profile button
    items.push({
      path: '/profile',
      icon: User,
      label: 'Profile',
      active: location.pathname.startsWith('/profile') || location.pathname.startsWith('/settings') || location.pathname === '/messaging' || location.pathname === '/wallet',
      onClick: undefined as ((e: React.MouseEvent) => void) | undefined
    });

    return items;
  }, [location.pathname, location.search, wishlistCount, totalItems, unreadCount, currentUser]);

  // Security: Validate navigation items
  const validatedNavItems = navItems.map(item => {
    const pathValidation = validateString(item.path, 'Navigation path', 1, 100);
    const labelValidation = validateString(item.label, 'Navigation label', 1, 50);
    
    if (!pathValidation.valid || !labelValidation.valid) {
      // SECURITY: Removed console.warn to prevent information leakage
      return null;
    }
    
    const sanitizedPath = sanitizeString(item.path, 100);
    const sanitizedLabel = sanitizeString(item.label, 50);
    
    // Ensure path is always a string (fallback to original if sanitization fails)
    if (!sanitizedPath) {
      // SECURITY: Removed console.warn to prevent information leakage
      return null;
    }
    
    return {
      ...item,
      path: sanitizedPath,
      label: sanitizedLabel || item.label
    };
  }).filter((item): item is NonNullable<typeof item> => item !== null);

  // Get the actual count of validated navigation items to ensure grid matches
  const navItemCount = validatedNavItems.length;

  return (
    <nav className="md:hidden fixed inset-x-0 bottom-0 z-[9998] bg-white/95 backdrop-blur border-t border-gray-200 px-3 py-2.5 sm:px-6 sm:py-3 pb-[calc(8px+env(safe-area-inset-bottom))]">
      <div className="mx-auto max-w-7xl">
        <div className="grid" style={{ gridTemplateColumns: `repeat(${navItemCount}, minmax(0, 1fr))` }}>
        {validatedNavItems.map((item) => {
          const Icon = item.icon;
          
          // Security: Enhanced safe navigation with rate limiting
          const handleNavigation = (e: React.MouseEvent) => {
            // If item has custom onClick handler, use it
            if (item.onClick) {
              item.onClick(e);
              return;
            }
            
            const rateLimitKey = `nav_${item.path}`;
            const rateLimitResult = navigationRateLimiter.isAllowed(rateLimitKey);
            if (!rateLimitResult.allowed) {
              e.preventDefault();
              // SECURITY: Removed console.warn to prevent information leakage
              return;
            }

            // Removed verbose logging - navigation happens too frequently
          };
          
          const navContent = (
            <>
              <div className="relative flex items-center justify-center w-10 h-10 rounded-lg">
                <Icon className="w-5 h-5" />
                {item.badge && item.badge > 0 && (
                  <span className="absolute -top-1 -right-2 min-w-[14px] h-4 px-1 rounded-full bg-[#da2516] text-white text-[10px] leading-none flex items-center justify-center">
                    {/* Security: Enhanced sanitize badge display */}
                    {item.badge > 99 ? '99+' : String(item.badge)}
                  </span>
                )}
              </div>
              {/* Security: Enhanced sanitize label display */}
              <span className="leading-none">{sanitizeString(item.label, 50)}</span>
            </>
          );
          
          // Use button for items with onClick (like Messages), Link for regular navigation
          if (item.onClick) {
            return (
              <button
                key={item.path}
                onClick={handleNavigation}
                className={`flex flex-col items-center justify-center h-[64px] text-xs ${
                  item.active 
                    ? 'text-[#da2516]' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                aria-label={item.label}
              >
                {navContent}
              </button>
            );
          }
          
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={handleNavigation}
              className={`flex flex-col items-center justify-center h-[64px] text-xs ${
                item.active 
                  ? 'text-[#da2516]' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {navContent}
            </Link>
          );
        })}
        </div>
      </div>
    </nav>
  );
};

export default MobileBottomNav;
